import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    Section,
    AddFilterButton,
    Divider,
    LinkButton,
    useFilterBarCollapse,
} from "@impero-com/ui";
import { Checkbox } from "antd";
import { AnimatePresence } from "motion/react";

import { DashboardFilters as DashboardFiltersData } from "api/generated/model";
import { FilterRow } from "components/filters/FilterRow";
import { localizeFilterType } from "components/filters/filterType";
import { TimeFilter } from "components/filters/TimeFilter";
import { useFilterRows } from "components/filters/useFilterRows";
import {
    countActiveFilters,
    deriveFilterTypes,
    deriveOnChangeCallbacks,
} from "org/dashboard/utils/pageStateUtils";
import { DEFAULT_DASHBOARD_FILTERS } from "org/pageState/defaults";

import { ExtractedTagCategory } from "../../types";
import { getEnabledFilterTypes } from "../../utils/filterUtils";

import { DashboardFilterBar } from "./DashboardFilterBar";
import * as theme from "./DashboardFilters.css";

interface DashboardFiltersProps {
    filters: DashboardFiltersData;
    onFiltersChange: (newFilters: DashboardFiltersData) => void;
    extractedTagCategories: ExtractedTagCategory[];
    controlEntitiesEnabled: boolean;
}

type ContentFilter = "ShowInactivePrograms" | "ShowArchivedActivities";
interface ContentFilterOption {
    label: string;
    key: ContentFilter;
    checked: boolean;
    onChange: (checked: boolean) => void;
}

export function DashboardFilters({
    filters,
    onFiltersChange,
    extractedTagCategories,
    controlEntitiesEnabled,
}: DashboardFiltersProps) {
    const { t } = useTranslation();
    const enabledFilterTypes = getEnabledFilterTypes(controlEntitiesEnabled);

    const {
        onTimePeriodChange,
        onShowInactiveProgramsChange,
        onShowArchivedActivitiesChange,
        onControlProgramsChange,
        onTagsChange,
        onEntitiesChange,
        resetFilterType,
    } = deriveOnChangeCallbacks(filters, onFiltersChange);

    // A filter row is shown for every filter that has a value, plus any row the
    // user is about to fill in.
    const {
        rows: selectedFilterTypes,
        addRow: addSelectedFilterType,
        removeRow: removeSelectedFilterType,
        replaceRow: onFilterTypeChange,
        clearRows,
    } = useFilterRows(() => deriveFilterTypes(filters), resetFilterType);

    const clearAllFilters = () => {
        onFiltersChange({
            showInactivePrograms:
                DEFAULT_DASHBOARD_FILTERS.showInactivePrograms,
            showArchivedActivities:
                DEFAULT_DASHBOARD_FILTERS.showArchivedActivities,
            timePeriod: DEFAULT_DASHBOARD_FILTERS.timePeriod,
            controlPrograms: DEFAULT_DASHBOARD_FILTERS.controlPrograms,
            tags: DEFAULT_DASHBOARD_FILTERS.tags,
            entities: DEFAULT_DASHBOARD_FILTERS.entities,
        });
        clearRows();
    };

    const availableFilters = enabledFilterTypes.filter(
        (ft) => !selectedFilterTypes.includes(ft)
    );

    const addFilterItems = availableFilters.map((f) => ({
        label: localizeFilterType(f, t),
        key: f,
        onClick: () => addSelectedFilterType(f),
    }));

    const contentFilterOptions: ContentFilterOption[] = [
        {
            label: t("Dashboard_ContentFilter_OptionShowInactivePrograms"),
            key: "ShowInactivePrograms",
            checked: filters.showInactivePrograms,
            onChange: onShowInactiveProgramsChange,
        },
        {
            label: t("Dashboard_ContentFilter_OptionShowArchivedActivities"),
            key: "ShowArchivedActivities",
            checked: filters.showArchivedActivities,
            onChange: onShowArchivedActivitiesChange,
        },
    ];

    const hasFiltersToAdd = availableFilters.length > 0;
    const activeFilterCount = countActiveFilters(filters);

    const [collapsed, setCollapsed] = useFilterBarCollapse("dashboard", true);

    return (
        <Section>
            <div className={theme.body}>
                <DashboardFilterBar
                    collapsed={collapsed}
                    onCollapsedChange={setCollapsed}
                    addLabel={t("FilterBar_AddFilter")}
                    hideFiltersLabel={t("FilterBar_HideFilters")}
                    getFilterCountLabel={(count) =>
                        t("FilterBar_FiltersApplied", { count })
                    }
                    addFilterItems={addFilterItems}
                    filterCount={activeFilterCount}
                    onClearFilters={clearAllFilters}
                    clearable={activeFilterCount > 0}
                >
                    <div className={theme.filtersContainer}>
                        <TimeFilter
                            value={filters.timePeriod}
                            onChange={onTimePeriodChange}
                        />
                        <AnimatePresence initial={false}>
                            {selectedFilterTypes.map((filterType, idx) => (
                                <FilterRow
                                    key={`${filterType}-${idx}`}
                                    filterType={filterType}
                                    index={idx}
                                    enabledFilterTypes={enabledFilterTypes}
                                    selectedFilters={selectedFilterTypes}
                                    onFilterRemove={removeSelectedFilterType}
                                    onFilterTypeChange={onFilterTypeChange}
                                    selectedTags={filters.tags}
                                    onTagsChange={onTagsChange}
                                    selectedControlPrograms={
                                        filters.controlPrograms
                                    }
                                    onControlProgramsChange={
                                        onControlProgramsChange
                                    }
                                    selectedEntities={filters.entities}
                                    onEntitiesChange={onEntitiesChange}
                                    tagCategories={extractedTagCategories}
                                />
                            ))}
                        </AnimatePresence>
                        <div className={theme.checkboxRow}>
                            {contentFilterOptions.map((opt) => (
                                <Checkbox
                                    key={opt.key}
                                    checked={opt.checked}
                                    onChange={(e) =>
                                        opt.onChange(e.target.checked)
                                    }
                                >
                                    {opt.label}
                                </Checkbox>
                            ))}
                        </div>
                    </div>

                    <Divider />

                    <div className={theme.filtersButtons}>
                        {hasFiltersToAdd && (
                            <AddFilterButton
                                items={addFilterItems}
                                addLabel={t("FilterBar_AddFilter")}
                            />
                        )}
                        {activeFilterCount > 0 && (
                            <LinkButton onClick={clearAllFilters}>
                                {t("FilterBar_ClearAll")}
                            </LinkButton>
                        )}
                    </div>
                </DashboardFilterBar>
            </div>
        </Section>
    );
}
